import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { DEBUG_PAGES, EDITOR_PAGES, type DebugPagePath, type EditorPagePath } from '@sorrem-client/ui/hooks';
import { GameStateBridge } from '@sorrem-client/ui/shared/game-state-bridge';
import { useOAuthCallback } from '@sorrem-client/ui/features/LoginPage/hooks';
// Each page from its own feature, not the features barrel (which exports this file too).
import { LoginPage, OAuthAccessDeniedPage } from '@sorrem-client/ui/features/LoginPage';
import { IntroProclamationPage, INTRO_PROCLAMATION } from '@sorrem-client/ui/features/IntroProclamationPage';
import { CharacterHome } from '@sorrem-client/ui/features/CharacterHome';
import { CrucibleJoinPage } from '@sorrem-client/ui/features/CrucibleJoinPage';
import { BugReportHost } from '@sorrem-client/ui/features/BugReport';
import { DeveloperSnapshotKeys } from '@sorrem-client/ui/features/DeveloperSnapshot';
import {
    useAppNavigate,
    canUseEditors,
    useAuthSessionCheck,
    useDeveloperAccess,
    useEditorAccess,
    useNamedItemCatalog,
    useSyncAudioSettings,
} from '@sorrem-client/ui/hooks';
import {
    getStoredSessionToken,
    logout,
    persistAuthSession,
    getStoredAccountId,
    subscribeAuthSessionExpired,
} from '@sorrem-client/ui/shared/auth-session';
import { rememberPostLoginPath, getPostLoginPath } from '@sorrem-client/ui/shared/post-login-path';
import { AppNavigate, AsyncStateMessage } from '@sorrem-client/ui/components';
import { sorremApi, useAppDispatch, useGetAuthSessionQuery } from '@sorrem-client/ui/store';
import type { AuthSessionResponse } from '@sorrem/shared';
import { endGameSession } from '@sorrem-client/game/game-session-store';
import { loadAccountCharacterCount, loadAccountSettings, markIntroProclamationSeen } from './account-landing';
import { DeveloperAccessDeniedPage, type GatedPageSet } from './DeveloperAccessDeniedPage';
import { DeveloperPageLayout } from './DeveloperPageLayout';
import styles from './AppRoutes.module.scss';

// Role-gated surfaces (the debug pages, the editors and the lore): lazy-loaded as their
// own chunks so they don't add weight to the main bundle every player
// downloads. Nothing may import them statically (the features barrel
// leaves them out for this reason), or the split is lost.
const MapDebugPage = lazy(() => import('../debug/map/MapDebugPage').then((m) => ({ default: m.MapDebugPage })));
const DebugOverlayPage = lazy(() => import('../debug/overlay').then((m) => ({ default: m.DebugOverlayPage })));
const CharacterDebugPage = lazy(() => import('../debug/character/CharacterDebugPage').then((m) => ({ default: m.CharacterDebugPage })));
const VfxDebugPage = lazy(() => import('../debug/vfx/VfxDebugPage').then((m) => ({ default: m.VfxDebugPage })));
const SoundDebugPage = lazy(() => import('../debug/sound/SoundDebugPage').then((m) => ({ default: m.SoundDebugPage })));
const NamedItemDesignerPage = lazy(() => import('../debug/named-items').then((m) => ({ default: m.NamedItemDesignerPage })));
const ItemEditorPage = lazy(() => import('../debug/item-editor').then((m) => ({ default: m.ItemEditorPage })));
const LorePage = lazy(() => import('../LorePage/LorePage').then((m) => ({ default: m.LorePage })));

function LoginRoute(): JSX.Element {
    const navigate = useAppNavigate();
    const dispatch = useAppDispatch();
    const oauthCallbackResult = useOAuthCallback();

    useEffect(() => {
        if (oauthCallbackResult.status !== 'success') {
            return;
        }

        persistAuthSession(
            oauthCallbackResult.data.accountId,
            oauthCallbackResult.data.token,
        );
        void loadAccountSettings(dispatch, oauthCallbackResult.data.accountId)
            .then(async (settings) => {
                if (settings !== null && !settings.hasSeenIntroProclamation) {
                    navigate('/intro', { replace: true });
                    return;
                }

                const characterCount = await loadAccountCharacterCount(dispatch, oauthCallbackResult.data.accountId);
                // An invite (#216) is followed once there is a character to join with; it is kept until then.
                navigate(characterCount === 0 ? '/character/create' : getPostLoginPath(), { replace: true });
            })
            .catch(() => {
                navigate('/character/overview', { replace: true });
            });
    }, [dispatch, navigate, oauthCallbackResult]);

    if (getStoredAccountId() !== null && getStoredSessionToken() !== null) {
        return <AppNavigate to={getPostLoginPath()} replace />;
    }

    const handleContinue = (): void => {
        const returnTo = `${window.location.origin}/login`;
        window.location.assign(`/api/auth/login/discord?returnTo=${encodeURIComponent(returnTo)}`);
    };

    if (oauthCallbackResult.status === 'denied' && oauthCallbackResult.data.reason === 'missing_role') {
        return (
            <OAuthAccessDeniedPage
                requiredRoleName={oauthCallbackResult.data.requiredRoleName}
                onRetry={handleContinue}
            />
        );
    }

    return <LoginPage buttonLabel="Sign in with Discord" onContinue={handleContinue} />;
}

/**
 * First-login intro: shows the Dominion's proclamation, records it as seen,
 * then continues to character creation (or Character Home for an account
 * that already has characters). A failed save never traps the player here;
 * they'd just see the proclamation again next login.
 */
function IntroRoute(): JSX.Element {
    const navigate = useAppNavigate();
    const dispatch = useAppDispatch();
    const [isContinuing, setIsContinuing] = useState(false);
    const accountId = getStoredAccountId();

    if (accountId === null || getStoredSessionToken() === null) {
        return <AppNavigate to="/login" replace />;
    }

    const handleContinue = (): void => {
        setIsContinuing(true);
        void markIntroProclamationSeen(dispatch, accountId)
            .catch(() => undefined)
            .then(() => loadAccountCharacterCount(dispatch, accountId))
            .then((characterCount) => {
                navigate(characterCount === 0 ? '/character/create' : getPostLoginPath(), { replace: true });
            })
            .catch(() => {
                navigate('/character/overview', { replace: true });
            });
    };

    return <IntroProclamationPage proclamation={INTRO_PROCLAMATION} isContinuing={isContinuing} onContinue={handleContinue} />;
}

/** The page's audio, on every route: the account's volumes, the sound catalog and UI clicks (#260). */
function AudioSettingsSync(): null {
    useSyncAudioSettings();
    return null;
}

/**
 * Sends the player back to /login whenever the server rejects the stored
 * token (see expireAuthSession), and drops the cached account data that
 * belonged to it. Lives inside the router so it can navigate.
 */
function AuthSessionExpiryRedirect(): null {
    const navigate = useAppNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => subscribeAuthSessionExpired(() => {
        // An invite opened with an expired session comes back after login, as when signed out (#216).
        rememberPostLoginPath(window.location.pathname);
        dispatch(sorremApi.util.resetApiState());
        navigate('/login', { replace: true });
    }), [dispatch, navigate]);

    return null;
}

/** Shown while a route waits: the session being confirmed, or a lazy-loaded page's chunk downloading. */
function RouteLoading(): JSX.Element {
    return (
        <div className={styles.routeLoading}>
            <AsyncStateMessage isLoading loadingMessage="Loading..." />
        </div>
    );
}

/**
 * Layout route for every signed-in screen: confirms the stored token with
 * the server once when the app loads (or after login) and redirects to
 * /login if it is missing or rejected. Child navigation keeps this mounted,
 * so switching tabs doesn't re-check.
 */
function RequireAuthRoute(): JSX.Element {
    const status = useAuthSessionCheck();
    const location = useLocation();
    // Named copies (item cards, crafting, the game) read the named item catalog, so it is in before any screen.
    const namedItems = useNamedItemCatalog(status !== 'valid');

    if (status === 'invalid') {
        // An invite link opened while signed out comes back after login (#216).
        rememberPostLoginPath(location.pathname);
        return <AppNavigate to="/login" replace />;
    }

    if (status === 'checking' || !namedItems.isReady) {
        return <RouteLoading />;
    }

    return <Outlet />;
}

/** Whether a session may open each role-gated set of pages. */
const PAGE_SET_ACCESS: Record<GatedPageSet, (session: AuthSessionResponse | undefined) => boolean> = {
    developer: (session) => session?.isDeveloper === true,
    editor: canUseEditors,
};

/**
 * Gates a role-gated route behind the signed-in account's Discord roles: a
 * developer route (/debug/*, /lore) behind the developer role (see the
 * server's DISCORD_DEVELOPER_ROLE_ID / isDeveloper claim), an editor route
 * (/editors/*) behind the creator role (DISCORD_CREATOR_ROLE_ID / isCreator)
 * or the developer role.
 * Redirects to /login if not authenticated at all, or shows an access-denied
 * page (rather than silently redirecting) if authenticated without the role.
 * On a local dev server (`import.meta.env.DEV`, false in any real build) a
 * visitor with no session still asks GET /api/auth/session, which answers a
 * token-less request with the server's dev-mode developer profile (see
 * authentication-api-controller.ts).
 */
function RequireRoleRoute({ pageSet, children }: { pageSet: GatedPageSet; children: JSX.Element }): JSX.Element {
    const navigate = useAppNavigate();
    const hasStoredSession = getStoredAccountId() !== null && getStoredSessionToken() !== null;
    const isAuthenticated = hasStoredSession || import.meta.env.DEV;
    const { data, isLoading } = useGetAuthSessionQuery(undefined, { skip: !isAuthenticated });
    const hasAccess = PAGE_SET_ACCESS[pageSet](data);
    const namedItems = useNamedItemCatalog(!hasAccess);

    if (!isAuthenticated) {
        return <AppNavigate to="/login" replace />;
    }

    if (isLoading || !namedItems.isReady) {
        return <RouteLoading />;
    }

    if (!hasAccess) {
        return <DeveloperAccessDeniedPage pageSet={pageSet} onReturn={() => navigate('/character/overview', { replace: true })} />;
    }

    return children;
}

/**
 * Layout route for the developer pages: gates them behind the developer role
 * and frames each in the shared header (see DeveloperPageLayout).
 */
function DeveloperRoute({ onLogout }: { onLogout: () => Promise<void> }): JSX.Element {
    const { debugPages } = useDeveloperAccess();

    return (
        <RequireRoleRoute pageSet="developer">
            <DeveloperPageLayout pages={debugPages} onLogout={onLogout}>
                <Suspense fallback={<RouteLoading />}>
                    <Outlet />
                </Suspense>
            </DeveloperPageLayout>
        </RequireRoleRoute>
    );
}

/**
 * Layout route for the editors: gates them behind the creator role (or the
 * developer role) and frames each in the shared header, its tabs the editors.
 */
function EditorRoute({ onLogout }: { onLogout: () => Promise<void> }): JSX.Element {
    const { editorPages } = useEditorAccess();

    return (
        <RequireRoleRoute pageSet="editor">
            <DeveloperPageLayout pages={editorPages} onLogout={onLogout}>
                <Suspense fallback={<RouteLoading />}>
                    <Outlet />
                </Suspense>
            </DeveloperPageLayout>
        </RequireRoleRoute>
    );
}

/** An invite link (`/join/<code>`): opens the Crucibles tab on the code, which finds its crucible. */
function JoinRoute(): JSX.Element {
    const { inviteCode = '' } = useParams<{ inviteCode: string }>();
    return <AppNavigate to={`/character/crucibles/join/${encodeURIComponent(inviteCode)}`} replace />;
}

function DebugMapRoute(): JSX.Element {
    const bridgeRef = useRef<GameStateBridge>(new GameStateBridge());

    return <MapDebugPage bridge={bridgeRef.current} />;
}

/** Each developer page's element; a page in `DEBUG_PAGES` without one does not compile. */
const DEBUG_PAGE_ELEMENTS: Readonly<Record<DebugPagePath, JSX.Element>> = {
    '/debug/maps': <DebugMapRoute />,
    '/debug/overlay': <DebugOverlayPage />,
    '/debug/characters': <CharacterDebugPage />,
    '/debug/vfx': <VfxDebugPage />,
    '/debug/sounds': <SoundDebugPage />,
    '/lore': <LorePage />,
};

/** Each editor's element; an editor in `EDITOR_PAGES` without one does not compile. */
const EDITOR_PAGE_ELEMENTS: Readonly<Record<EditorPagePath, JSX.Element>> = {
    '/editors/items': <ItemEditorPage />,
    '/editors/named-items': <NamedItemDesignerPage />,
};

export function AppRoutes(): JSX.Element {
    // Resolves once the session is cleared, so the caller can load /login
    // only after that (a fresh page would otherwise still see the session).
    const handleLogout = useCallback(async (): Promise<void> => {
        await endGameSession();
        await logout();
    }, []);

    return (
        <BrowserRouter>
            <AuthSessionExpiryRedirect />
            <AudioSettingsSync />
            <DeveloperSnapshotKeys />
            <BugReportHost>
                <Routes>
                    <Route path="/login" element={<LoginRoute />} />
                    <Route element={<DeveloperRoute onLogout={handleLogout} />}>
                        {DEBUG_PAGES.map(({ path }) => <Route key={path} path={path} element={DEBUG_PAGE_ELEMENTS[path]} />)}
                        <Route path="/lore/:slug" element={<LorePage />} />
                    </Route>
                    <Route element={<EditorRoute onLogout={handleLogout} />}>
                        {EDITOR_PAGES.map(({ path }) => <Route key={path} path={path} element={EDITOR_PAGE_ELEMENTS[path]} />)}
                    </Route>
                    <Route element={<RequireAuthRoute />}>
                        <Route path="/intro" element={<IntroRoute />} />
                        <Route path="/character" element={<AppNavigate to="/character/overview" replace />} />
                        <Route path="/character/create" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/character/crucibles/create" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/character/crucibles/:selectedId" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/character/journey/cutscenes/:cutsceneId" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/character/journey/visions/:visionId" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/character/:tab" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/character/crucibles/join/:inviteCode" element={<CharacterHome onLogout={handleLogout} />} />
                        <Route path="/join/:inviteCode" element={<JoinRoute />} />
                        <Route path="/crucibles/:instanceId" element={<CrucibleJoinPage />} />
                    </Route>
                    <Route path="*" element={<AppNavigate to="/login" replace />} />
                </Routes>
            </BugReportHost>
        </BrowserRouter>
    );
}

